import { useEffect } from 'react';
import { PushNotifications } from '@capacitor/push-notifications';
import { Capacitor } from '@capacitor/core';

const FCM_API_URL = 'https://app.phanh.app/api/auth/fcm-token';
const REPORT_URL = 'https://app.phanh.app/api/notifications/report-device-status';

const getAuthToken = () => localStorage.getItem('authToken');

// Send status report to backend so we can debug without Xcode
const reportStatus = async (data: Record<string, any>) => {
  try {
    const authToken = getAuthToken();
    if (!authToken) return;
    await fetch(REPORT_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken}`,
      },
      body: JSON.stringify({ platform: Capacitor.getPlatform(), ...data }),
    });
  } catch (_) { /* silent — don't crash the app */ }
};

export const usePushNotifications = () => {
  useEffect(() => {
    let isMounted = true;
    const logs: string[] = [];
    const log = (msg: string) => {
      console.log('[Push]', msg);
      logs.push(`${new Date().toISOString()}: ${msg}`);
    };

    const setupPush = async () => {
      if (Capacitor.getPlatform() === 'web') return;

      try {
        log('Starting push setup...');

        // Step 1: Check permission
        let permStatus = await PushNotifications.checkPermissions();
        log(`Permission status: ${permStatus.receive}`);

        if (permStatus.receive === 'prompt') {
          permStatus = await PushNotifications.requestPermissions();
          log(`After request, permission: ${permStatus.receive}`);
        }

        if (permStatus.receive !== 'granted') {
          log('Permission NOT granted — cannot register for push');
          await reportStatus({ permissionStatus: permStatus.receive, logs });
          return;
        }

        await reportStatus({ permissionStatus: 'granted', logs });

        // Step 2: Attach listeners BEFORE calling register()
        PushNotifications.addListener('registrationError', async (error: any) => {
          const errMsg = JSON.stringify(error);
          log(`Registration error: ${errMsg}`);
          await reportStatus({ permissionStatus: 'granted', fcmError: errMsg, logs });
        });

        PushNotifications.addListener('registration', async (apnsTokenData) => {
          if (!isMounted) return;
          const apnsToken = apnsTokenData.value;
          log(`Got APNs token: ${apnsToken?.substring(0, 20)}...`);

          let tokenToSave = apnsToken;
          let fcmError: string | null = null;

          // Step 3: Exchange APNs → FCM token (iOS only)
          if (Capacitor.getPlatform() === 'ios') {
            for (let attempt = 1; attempt <= 5; attempt++) {
              try {
                log(`FCM.getToken attempt ${attempt}/5...`);
                const { FCM } = await import('@capacitor-community/fcm');
                const result = await FCM.getToken();

                if (result?.token && result.token.length > 50) {
                  tokenToSave = result.token;
                  log(`FCM token obtained on attempt ${attempt}: ${tokenToSave.substring(0, 20)}...`);
                  fcmError = null;
                  break;
                } else {
                  fcmError = `Attempt ${attempt}: FCM.getToken returned empty/short token`;
                  log(fcmError);
                }
              } catch (err: any) {
                fcmError = `Attempt ${attempt}: ${err?.message || String(err)}`;
                log(`FCM.getToken failed: ${fcmError}`);
              }

              const delay = Math.pow(2, attempt - 1) * 1000;
              log(`Waiting ${delay}ms before retry...`);
              await new Promise(resolve => setTimeout(resolve, delay));
            }

            const isApnsToken = /^[0-9A-F]{64}$/i.test(tokenToSave?.trim() || '');
            if (isApnsToken) {
              log('WARNING: Still have APNs token after all retries — cannot use for FCM!');
              fcmError = fcmError || 'All FCM.getToken attempts failed, using APNs token as fallback';
            }
          }

          // Step 4: Save to backend
          const authToken = getAuthToken();
          if (!authToken) {
            log('ERROR: No authToken in localStorage — cannot save token to backend');
            await reportStatus({ permissionStatus: 'granted', apnsToken, fcmToken: null, fcmError, backendStatus: 'no_auth_token', logs });
            return;
          }

          try {
            log(`Posting token to backend (length: ${tokenToSave?.length})...`);
            const response = await fetch(FCM_API_URL, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${authToken}`,
              },
              body: JSON.stringify({ token: tokenToSave, platform: Capacitor.getPlatform() }),
            });

            const backendStatus = `HTTP ${response.status}`;
            const responseText = await response.text();
            log(`Backend response: ${backendStatus} — ${responseText.substring(0, 100)}`);

            await reportStatus({
              permissionStatus: 'granted',
              apnsToken,
              fcmToken: tokenToSave !== apnsToken ? tokenToSave : null,
              fcmError,
              backendStatus: `${backendStatus}: ${responseText.substring(0, 80)}`,
              logs
            });
          } catch (fetchErr: any) {
            const errMsg = fetchErr?.message || String(fetchErr);
            log(`Backend fetch failed: ${errMsg}`);
            await reportStatus({ permissionStatus: 'granted', apnsToken, fcmError, backendStatus: `fetch_error: ${errMsg}`, logs });
          }
        });

        PushNotifications.addListener('pushNotificationReceived', (notification) => {
          log(`Foreground notification: ${notification.title}`);
        });

        PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
          log(`Notification tapped: ${action.notification?.title}`);
        });

        // Step 5: Trigger registration
        log('Calling PushNotifications.register()...');
        await PushNotifications.register();
        log('register() called — waiting for registration event...');

      } catch (e: any) {
        const errMsg = e?.message || String(e);
        log(`Fatal error: ${errMsg}`);
        await reportStatus({ fcmError: `Fatal: ${errMsg}`, logs });
      }
    };

    setupPush();

    return () => {
      isMounted = false;
      if (Capacitor.getPlatform() !== 'web') {
        PushNotifications.removeAllListeners();
      }
    };
  }, []);
};
